import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaCogs, FaSearch } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './ManufacturingCategories.css';

const ManufacturingCategories = () => {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewingCategory, setViewingCategory] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    status: 'active',
    rawItems: [],
    finalProductInventory: '',
    finalProductionQuantity: ''
  });

  const [inventoryOptions, setInventoryOptions] = useState([]);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const formatDateDDMMYY = (isoString) => {
    if (!isoString) return 'N/A';
    const d = new Date(isoString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    // Preload inventory options for selects
    const fetchInventory = async () => {
      try {
        const resp = await api.get('/api/inventory');
        const items = resp.data?.inventoryItems || resp.data?.items || resp.data || [];
        setInventoryOptions(Array.isArray(items) ? items : []);
      } catch (e) {
        // best-effort only
        setInventoryOptions([]);
      }
    };
    fetchInventory();
  }, []);

  // Reset/clamp page when filters or data change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Auto-fetch when filters change (debounced)
  useEffect(() => {
    const id = setTimeout(() => {
      loadCategories();
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / itemsPerPage));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [categories, currentPage, itemsPerPage]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/api/manufacturing-categories?${params.toString()}`);
      setCategories(response.data.manufacturingCategories || []);
      setError('');
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load manufacturing categories');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this manufacturing category?')) {
      try {
        await api.delete(`/api/manufacturing-categories/${id}`);
        setCategories(categories.filter(c => c._id !== id));
      } catch (err) {
        console.error('Error deleting category:', err);
        setError('Failed to delete manufacturing category');
      }
    }
  };

  const openCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      code: '',
      status: 'active',
      rawItems: [{ inventory: '', qualityOrWeight: '', quantity: '', rate: '' }],
      finalProductInventory: '',
      finalProductionQuantity: ''
    });
    setShowCreateModal(true);
    setError('');
  };

  const openEditModal = async (category) => {
    try {
      setLoading(true);
      setError('');
      const resp = await api.get(`/api/manufacturing-categories/${category._id}`);
      const fresh = resp.data?.manufacturingCategory || category;

      setEditingCategory(fresh);
      setFormData({
        name: fresh.name || '',
        description: fresh.description || '',
        code: fresh.code || '',
        status: fresh.status || 'active',
        rawItems: (fresh.rawItems || []).map(ri => ({
          inventory: ri.inventory?._id || ri.inventory || '',
          qualityOrWeight: ri.qualityOrWeight || '',
          quantity: ri.quantity ?? '',
          rate: ri.rate ?? ''
        })),
        finalProductInventory: fresh.finalProductInventory?._id || fresh.finalProductInventory || '',
        finalProductionQuantity: fresh.finalProductionQuantity ?? ''
      });
      setShowModal(true);
    } catch (err) {
      console.error('Error opening edit modal:', err);
      // Fallback to existing object
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        code: category.code || '',
        status: category.status || 'active',
        rawItems: (category.rawItems || []).map(ri => ({
          inventory: ri.inventory?._id || ri.inventory || '',
          qualityOrWeight: ri.qualityOrWeight || '',
          quantity: ri.quantity ?? '',
          rate: ri.rate ?? ''
        })),
        finalProductInventory: category.finalProductInventory?._id || category.finalProductInventory || '',
        finalProductionQuantity: category.finalProductionQuantity ?? ''
      });
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (category) => {
    setViewingCategory(category);
  };

  const openLedgerForCategory = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setShowCreateModal(false);
    setEditingCategory(null);
    setViewingCategory(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRawItemChange = (index, field, value) => {
    setFormData(prev => {
      const next = { ...prev };
      const copy = Array.isArray(next.rawItems) ? [...next.rawItems] : [];
      copy[index] = { ...(copy[index] || {}), [field]: value };
      next.rawItems = copy;
      return next;
    });
  };

  const addRawItemRow = () => {
    setFormData(prev => ({
      ...prev,
      rawItems: [...(prev.rawItems || []), { inventory: '', qualityOrWeight: '', quantity: '', rate: '' }]
    }));
  };

  const removeRawItemRow = (index) => {
    setFormData(prev => ({
      ...prev,
      rawItems: (prev.rawItems || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.name || !formData.code) {
        throw new Error('Please fill in all required fields');
      }

      if (editingCategory) {
        const payload = {
          ...formData,
          rawItems: (formData.rawItems || [])
            .filter(r => r && r.inventory)
            .map(r => ({
              inventory: r.inventory,
              qualityOrWeight: r.qualityOrWeight,
              quantity: r.quantity === '' ? undefined : Number(r.quantity),
              rate: r.rate === '' ? undefined : Number(r.rate)
            })),
          finalProductInventory: formData.finalProductInventory || undefined,
          finalProductionQuantity: formData.finalProductionQuantity === '' ? undefined : Number(formData.finalProductionQuantity)
        };
        const response = await api.put(`/api/manufacturing-categories/${editingCategory._id}`, payload);
        setCategories(categories.map(c => c._id === editingCategory._id ? response.data.manufacturingCategory : c));
      } else {
        const payload = {
          ...formData,
          rawItems: (formData.rawItems || [])
            .filter(r => r && r.inventory)
            .map(r => ({
              inventory: r.inventory,
              qualityOrWeight: r.qualityOrWeight,
              quantity: r.quantity === '' ? undefined : Number(r.quantity),
              rate: r.rate === '' ? undefined : Number(r.rate)
            })),
          finalProductInventory: formData.finalProductInventory || undefined,
          finalProductionQuantity: formData.finalProductionQuantity === '' ? undefined : Number(formData.finalProductionQuantity)
        };
        const response = await api.post('/api/manufacturing-categories', payload);
        setCategories([...categories, response.data.manufacturingCategory]);
      }

      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save manufacturing category');
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(c =>
    String(c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    String(c.code || '').toLowerCase().includes(search.toLowerCase()) ||
    String(c.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedCategories = filteredCategories.slice(pageStart, pageEnd);

  if (loading && categories.length === 0) return <Loading />;

  return (
    <div className="manufacturing-categories-container">
      <div className="manufacturing-categories-header">
        <div>
          <h1 className="manufacturing-categories-title">Manufacturing Categories</h1>
          <p className="manufacturing-categories-subtitle">Create, edit, and manage manufacturing categories</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="add-category-btn" onClick={openCreateModal}>
            <FaPlus /> Create Category
          </button>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search categories by name, code, or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        
        <div className="filter-row">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <button onClick={loadCategories} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <div className="manufacturing-categories-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredCategories.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredCategories.length, pageEnd)} of {filteredCategories.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label htmlFor="pageSize" style={{ color: '#6c757d', fontSize: '0.9rem' }}>Rows per page:</label>
            <select
              id="pageSize"
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ minWidth: 100 }}
            >
              {[10, 20, 50, 100].map(sz => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
        <table className="manufacturing-categories-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center' }}>No manufacturing categories found.</td>
              </tr>
            ) : (
              paginatedCategories.map(category => (
                <tr key={category._id}>
                  <td>
                    <div className="category-name-cell">
                      <FaCogs className="category-icon" />
                      <span>{category.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="code-badge">{category.code}</span>
                  </td>
                  <td>{category.description || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${category.status}`}>
                      {category.status}
                    </span>
                  </td>
                  <td>{category.createdBy?.name || 'N/A'}</td>
                  <td>
                    {formatDateDDMMYY(category.createdAt)}
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button 
                        onClick={() => openViewModal(category)} 
                        className="action-btn view"
                        title="View Category"
                      >
                        <FaEye /> View
                      </button>
                      <button 
                        onClick={() => openEditModal(category)} 
                        className="action-btn edit"
                        title="Edit Category"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(category._id)} 
                        className="action-btn delete"
                        title="Delete Category"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showModal || showCreateModal) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Manufacturing Category' : 'Create New Manufacturing Category'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="category-form">
              <div className="form-section">
                <h3>Category Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter category name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="code">Code *</label>
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., ELEC, MECH, CHEM"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter category description (optional)"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Raw Materials</h3>
                {(formData.rawItems || []).map((ri, idx) => (
                  <div key={idx} className="form-row">
                    <div className="form-group">
                      <label>Raw Item</label>
                      <select
                        value={ri.inventory}
                        onChange={(e) => handleRawItemChange(idx, 'inventory', e.target.value)}
                      >
                        <option value="">Select inventory item</option>
                        {inventoryOptions.map(opt => (
                          <option key={opt._id} value={opt._id}>
                            {opt.itemName} ({opt.itemCode})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Quality/Weight</label>
                      <input
                        type="text"
                        value={ri.qualityOrWeight || ''}
                        onChange={(e) => handleRawItemChange(idx, 'qualityOrWeight', e.target.value)}
                        placeholder="e.g., 24K / 1.5kg"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantity</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={ri.quantity}
                        onChange={(e) => handleRawItemChange(idx, 'quantity', e.target.value)}
                        placeholder="Enter quantity"
                      />
                    </div>
                    <div className="form-group">
                      <label>Rate</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={ri.rate}
                        onChange={(e) => handleRawItemChange(idx, 'rate', e.target.value)}
                        placeholder="Enter rate"
                      />
                    </div>
                    <div className="form-group">
                      <label>&nbsp;</label>
                      <button type="button" className="btn-secondary" onClick={() => removeRawItemRow(idx)}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn-primary" onClick={addRawItemRow}>
                  <FaPlus /> Add Raw Item
                </button>
              </div>

              <div className="form-section">
                <h3>Final Production</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Final Product (Inventory)</label>
                    <select
                      name="finalProductInventory"
                      value={formData.finalProductInventory}
                      onChange={handleInputChange}
                    >
                      <option value="">Select inventory item</option>
                      {inventoryOptions.map(opt => (
                        <option key={opt._id} value={opt._id}>
                          {opt.itemName} ({opt.itemCode})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Final Production Quantity</label>
                    <input
                      type="number"
                      name="finalProductionQuantity"
                      min="0"
                      step="any"
                      value={formData.finalProductionQuantity}
                      onChange={handleInputChange}
                      placeholder="e.g., 100"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingCategory && (
        <div className="modal-overlay" onClick={() => setViewingCategory(null)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Category Details - {viewingCategory.name}</h2>
              <button className="modal-close" onClick={() => setViewingCategory(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="category-view-content">
              <div className="category-info-section">
                <div className="detail-group">
                  <h3>Category Information</h3>
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{viewingCategory.name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Code:</label>
                    <span>{viewingCategory.code}</span>
                  </div>
                  <div className="detail-item">
                    <label>Description:</label>
                    <span>{viewingCategory.description || 'No description'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingCategory.status}`}>
                      {viewingCategory.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Created By:</label>
                    <span>{viewingCategory.createdBy?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created At:</label>
                    <span>{formatDateDDMMYY(viewingCategory.createdAt)}</span>
                  </div>
                  {viewingCategory.updatedBy && (
                    <div className="detail-item">
                      <label>Last Updated By:</label>
                      <span>{viewingCategory.updatedBy.name}</span>
                    </div>
                  )}
                  {viewingCategory.updatedAt && (
                    <div className="detail-item">
                      <label>Last Updated At:</label>
                      <span>{formatDateDDMMYY(viewingCategory.updatedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="detail-group">
                <h3>Raw Materials</h3>
                {(!viewingCategory.rawItems || viewingCategory.rawItems.length === 0) ? (
                  <div className="detail-item"><span>No raw items added</span></div>
                ) : (
                  (viewingCategory.rawItems || []).map((ri, idx) => (
                    <div key={idx} className="detail-item">
                      <label>Item {idx + 1}:</label>
                      <span>
                        {(ri.inventory?.itemName || ri.inventory?.name || '')} {ri.inventory?.itemCode ? `(${ri.inventory.itemCode})` : ''}
                        {ri.qualityOrWeight ? ` | ${ri.qualityOrWeight}` : ''}
                        {ri.quantity !== undefined ? ` | Qty: ${ri.quantity}` : ''}
                        {ri.rate !== undefined ? ` | Rate: ${ri.rate}` : ''}
                      </span>
                    </div>
                  ))
                )}
              </div>
              <div className="detail-group">
                <h3>Final Production</h3>
                <div className="detail-item">
                  <label>Final Product:</label>
                  <span>
                    {viewingCategory.finalProductInventory ? `${viewingCategory.finalProductInventory.itemName} (${viewingCategory.finalProductInventory.itemCode})` : 'Not set'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Production Qty:</label>
                  <span>{viewingCategory.finalProductionQuantity ?? 'N/A'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
                <button className="btn-primary" onClick={openLedgerForCategory}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingCategory ? `Manufacturing category ${viewingCategory.code} - ${viewingCategory.name}` : ''}
      />
    </div>
  );
};

export default ManufacturingCategories;